import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { CoachLevel } from '@4cm/core/profiles';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export type Level = 'novice' | 'academy';
export type ThemeAccent = 'green' | 'blue' | 'purple';

export const THEME_ACCENTS: ThemeAccent[] = ['green', 'blue', 'purple'];

interface CalibrationState {
  level: Level;
  coachLevel: CoachLevel;
  themeAccent: ThemeAccent;
  setLevel: (next: Level) => void;
  setCoachLevel: (next: CoachLevel) => void;
  setThemeAccent: (next: ThemeAccent) => void;
}

const ACADEMY_LEVELS: CoachLevel[] = ['ADVANCED', 'ELITE_CAT1'];
const TOGGLE_TO_COACH_LEVEL: Record<Level, CoachLevel> = {
  novice: 'GRASSROOTS',
  academy: 'ELITE_CAT1',
};

const DEFAULT_COACH_LEVEL: CoachLevel = 'GRASSROOTS';
const DEFAULT_THEME_ACCENT: ThemeAccent = 'green';
const STORAGE_KEY = '4cm_calibration';
const ACCENT_STORAGE_KEY = '4cm_theme_accent';

const CalibrationContext = createContext<CalibrationState>({
  level: 'novice',
  coachLevel: DEFAULT_COACH_LEVEL,
  themeAccent: DEFAULT_THEME_ACCENT,
  setLevel: () => {},
  setCoachLevel: () => {},
  setThemeAccent: () => {},
});

function levelFromCoachLevel(c: CoachLevel): Level {
  return ACADEMY_LEVELS.includes(c) ? 'academy' : 'novice';
}

export function CalibrationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [coachLevel, setCoachLevelState] = useState<CoachLevel>(() => {
    const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return (cached as CoachLevel) ?? DEFAULT_COACH_LEVEL;
  });
  const [themeAccent, setThemeAccentState] = useState<ThemeAccent>(() => {
    const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(ACCENT_STORAGE_KEY) : null;
    return (THEME_ACCENTS as readonly string[]).includes(cached ?? '')
      ? (cached as ThemeAccent)
      : DEFAULT_THEME_ACCENT;
  });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      // coach_level lives on users table directly per migration 00025
      // (no separate coach_profiles table — see packages/db/migrations).
      const { data } = await supabase
        .from('users')
        .select('coach_level')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const dbLevel = (data?.coach_level as CoachLevel | null) ?? null;
      if (dbLevel) setCoachLevelState(dbLevel);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const level = levelFromCoachLevel(coachLevel);

  useEffect(() => {
    const html = document.documentElement;
    html.dataset.level = level;
    html.dataset.density = level === 'academy' ? 'workbench' : 'touchline';
  }, [level]);

  // Mirror the active accent onto <html> so tokens.css [data-theme-accent]
  // selectors apply globally — no per-component plumbing needed.
  useEffect(() => {
    document.documentElement.dataset.themeAccent = themeAccent;
  }, [themeAccent]);

  const setThemeAccent = (next: ThemeAccent) => {
    setThemeAccentState(next);
    try { localStorage.setItem(ACCENT_STORAGE_KEY, next); } catch { /* ignore */ }
  };

  const persistCoachLevel = (next: CoachLevel) => {
    setCoachLevelState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    if (user) {
      // ensureCoachUser ran on auth — the row exists, so update is fine.
      void supabase
        .from('users')
        .update({ coach_level: next })
        .eq('id', user.id);
    }
  };

  const setLevel = (next: Level) => persistCoachLevel(TOGGLE_TO_COACH_LEVEL[next]);

  return (
    <CalibrationContext.Provider
      value={{
        level,
        coachLevel,
        themeAccent,
        setLevel,
        setCoachLevel: persistCoachLevel,
        setThemeAccent,
      }}
    >
      {children}
    </CalibrationContext.Provider>
  );
}

export function useCalibration() {
  return useContext(CalibrationContext);
}

/**
 * Route-level guard for surfaces that are academy-only affordances.
 * If a novice lands here via a typed URL or stale Brain Bar GO, send them
 * to `fallback` rather than greying anything out (AGENTS.md "no disabled
 * buttons; hide unavailable features"). Used to wrap routes in App.tsx.
 */
export function AcademyOnlyRoute({
  children,
  fallback = '/',
}: {
  children: ReactNode;
  fallback?: string;
}) {
  const { level } = useCalibration();
  if (level === 'novice') return <Navigate to={fallback} replace />;
  return <>{children}</>;
}
